import { useState, useEffect } from 'react';
import { FilterOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { getIcon } from '@utils/image';
import Table from '@/components/ui/table/Table';
import { ActionLinks } from '@/components/ui/table/style';
import AnalyticsCard from '@/components/ui/analytics/AnalyticsCard';
import {
    DashboardContainer,
    KPICardsGrid,
    FirstRowContainer,
    SecondRowContainer,
    LeftColumn,
    ChartCard,
    ChartCardHeader,
    ChartCardTitle,
    ChartCardValue,
    ChartCardChange,
    RevenueChartContainer,
    RevenueChartYAxis,
    RevenueChartYAxisLabel,
    CountryChartContainer,
    DonutChartHeader,
    DonutChartHeaderLeft,
    DonutChartHeaderRight,
    DonutChartHeaderIcon,
    DonutChartHeaderButton,
    ChartsColumn,
    MiniBarChart,
    DotChart,
    DotChartColumn,
    DotChartDot,
    DotChartMonthLabel,
    CountryBar,
    CountryBarLabel,
    CountryBarValue,
    RevenueCountryTitle,
    CountrySpacer,
    CountryItem,
    CountryItemTop,
    CountryItemLeft,
    CountryFlag,
    CountryName,
    CountryRevenue,
    CountryProgressBar,
    CountryProgressFill,
    TotalRevenueHeader,
    TotalRevenueIcon,
    TotalRevenueContent,
    TotalRevenueTitleRow
} from './style';
import { Button } from '@/components/ui/Button';
import { Container } from '@/components/ui/Container';
import DonutChart from '@/components/ui/charts/DonutChart';
import { useDashboard } from '@/hooks/owner/useDashboard';

const Dashboard = () => {
    const { searchText, setSearchText } = useDashboard();
    const [circleSize, setCircleSize] = useState(40);
    const [gap, setGap] = useState(4);

    // Calculate responsive circle size and gap
    useEffect(() => {
        const updateSizes = () => {
            const width = window.innerWidth;
            if (width <= 480) {
                setCircleSize(28);
                setGap(2);
            } else if (width <= 768) {
                setCircleSize(32);
                setGap(3);
            } else if (width <= 1024) {
                setCircleSize(36);
                setGap(3);
            } else {
                setCircleSize(40);
                setGap(4);
            }
        };

        updateSizes();
        window.addEventListener('resize', updateSizes);
        return () => window.removeEventListener('resize', updateSizes);
    }, []);

    // KPI Mini Chart Data
    const mrrData = [45, 42, 40, 38, 35, 33, 30];
    const tenantsData = [100, 105, 110, 115, 118, 120, 124];
    const subscriptionsData = [75, 78, 80, 82, 84, 85, 87];
    const usersData = [520, 510, 505, 502, 500, 498, 500];

    // Revenue Dot Chart Data - from API (month and amount in USD)
    // TODO: Replace with actual API call
    const revenueDataFromAPI = [
        { month: 'Jan', amount: 100 },
        { month: 'Feb', amount: 80 },
        { month: 'Mar', amount: 100 },
        { month: 'Apr', amount: 60 },
        { month: 'May', amount: 100 },
        { month: 'Jun', amount: 40 },
        { month: 'Jul', amount: 20 },
        { month: 'Aug', amount: 20 },
        { month: 'Sep', amount: 120 },
        { month: 'Oct', amount: 80 },
        { month: 'Nov', amount: 80 },
        { month: 'Dec', amount: 100 }
    ];

    // Calculate max amount from data first
    const maxAmount = Math.max(...revenueDataFromAPI.map(d => d.amount || d.value || 0), 20);
    const minRevenueValue = 20; // Minimum Y-axis value
    const maxLabels = 6; // Always show exactly 6 labels
    const maxCircles = 6; // Always show exactly 6 circles maximum

    // Calculate Y-axis labels (always exactly 6 labels, evenly distributed from $20 to max amount)
    const yAxisLabels = [];
    const step = (maxAmount - minRevenueValue) / (maxLabels - 1);
    for (let i = maxLabels - 1; i >= 0; i--) {
        const value = minRevenueValue + (i * step);
        // Round to nearest integer
        yAxisLabels.push(Math.round(value));
    }

    // Scale circle counts to fit within 6 circles maximum
    // $20 should show 1 circle, maxAmount should show 6 circles
    // Each circle represents a portion of the range from $20 to maxAmount
    const amountRange = maxAmount - minRevenueValue;
    // We have 5 intervals (from 1 circle to 6 circles), so divide range by 5
    const amountPerCircle = amountRange / (maxCircles - 1);

    const revenueData = revenueDataFromAPI.map(item => {
        const amount = item.amount || item.value || 0;
        // Scale amount to circle count (1 to 6 circles)
        // $20 = 1 circle, maxAmount = 6 circles
        // amount = minRevenueValue + ((circles - 1) * amountPerCircle)
        // circles = 1 + ((amount - minRevenueValue) / amountPerCircle)
        let circles = 1 + Math.round((amount - minRevenueValue) / amountPerCircle);
        // Clamp between 1 and 6
        circles = Math.max(1, Math.min(maxCircles, circles));
        return {
            month: item.month,
            amount: amount,
            circles: circles
        };
    });

    // Donut Chart Data - calculate percentages from actual values
    const donutDataRaw = [
        { name: 'Enterprise', value: 19934, color: '#EC4C37' },
        { name: 'Pro', value: 20934, color: '#FFFFFF' },
        { name: 'Basic', value: 12934, color: '#66686A' }
    ];

    // Calculate total and percentages
    const donutTotal = donutDataRaw.reduce((sum, item) => sum + item.value, 0);
    const donutData = donutDataRaw.map(item => ({
        ...item,
        percentage: Math.round((item.value / donutTotal) * 100 * 100) / 100 // Round to 2 decimal places
    }));

    // Country Chart Data - matching image exactly
    const countryData = [
        { country: 'Canada', value: 13, displayValue: '13k', flag: 'canadaFlag' },
        { country: 'Australia', value: 64, displayValue: '64k', flag: 'australiaFlag' },
        { country: 'Japan', value: 34, displayValue: '34', flag: 'japanFlag' }
    ];

    const maxCountryValue = Math.max(...countryData.map(d => d.value));

    // Table Data
    const tableData = [
        {
            key: '1',
            tenant: 'Annette Black',
            plan: 'Basic',
            productId: '#ID-1234',
            status: 'Delivered',
            requestDate: '12 Jan 2023',
            issueDate: '12 Jan 2023'
        },
        {
            key: '2',
            tenant: 'Floyd Miles',
            plan: 'Premium',
            productId: '#ID-1234',
            status: 'Pending',
            requestDate: '4 Feb 2023',
            issueDate: '4 Feb 2023'
        },
        {
            key: '3',
            tenant: 'Robert Fox',
            plan: 'Pro',
            productId: '#ID-1234',
            status: 'In Review',
            requestDate: '18 Mar 2023',
            issueDate: '18 Mar 2023'
        },
        {
            key: '4',
            tenant: 'Darlene Robertson',
            plan: 'Pro',
            productId: '#ID-1234',
            status: 'In Review',
            requestDate: '27 Apr 2023',
            issueDate: '27 Apr 2023'
        },
        {
            key: '5',
            tenant: 'Marvin McKinney',
            plan: 'Basic',
            productId: '#ID-1234',
            status: 'Resolved',
            requestDate: '18 May 2023',
            issueDate: '18 May 2023'
        }
    ];

    const columns = [
        {
            title: 'Tenants',
            dataIndex: 'tenant',
            key: 'tenant',
        },
        {
            title: 'Pricing Plan',
            dataIndex: 'plan',
            key: 'plan',
        },
        {
            title: 'Product ID',
            dataIndex: 'productId',
            key: 'productId',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
        {
            title: 'Request Date',
            dataIndex: 'requestDate',
            key: 'requestDate',
        },
        {
            title: 'Issue Date',
            dataIndex: 'issueDate',
            key: 'issueDate',
        },
        {
            title: 'Action',
            key: 'action',
            render: () => (
                <ActionLinks>
                    <a>View</a>
                    <span>/</span>
                    <a>Respond</a>
                </ActionLinks>
            ),
        },
    ];


    return (
        <DashboardContainer>
            <FirstRowContainer>
                <LeftColumn>
                    <KPICardsGrid $columns={2} $rows={2} $height="450px">
                        <AnalyticsCard
                            title="Monthly Recurring Revenue"
                            value="$163K"
                            change={{
                                value: '-4%',
                                isPositive: false,
                                text: 'From last month'
                            }}
                            icon="revenueIcon"
                            chartData={[
                                { red: 25, grey: 45 },
                                { red: 40, grey: 70 },
                                { red: 35, grey: 50 },
                                { red: 55, grey: 70 },
                                { red: 30, grey: 50 },
                                { red: 45, grey: 55 },
                                { red: 20, grey: 60 }
                            ]}
                        />

                        <AnalyticsCard
                            title="Total Tenants"
                            value="124"
                            change={{
                                value: '+10%',
                                isPositive: true,
                                text: 'From last month'
                            }}
                            icon="tenantIcon"
                            chartData={[
                                { red: 25, grey: 75 },
                                { red: 40, grey: 60 },
                                { red: 35, grey: 65 },
                                { red: 50, grey: 50 },
                                { red: 30, grey: 70 },
                                { red: 45, grey: 55 },
                                { red: 40, grey: 60 }
                            ]}
                        />

                        <AnalyticsCard

                            title="Active Subscription"
                            value="87"
                            change={{
                                value: '+7%',
                                isPositive: true,
                                text: 'From last month'
                            }}
                            icon="subscriptionIcon"
                            chartData={[
                                { red: 25, grey: 75 },
                                { red: 40, grey: 60 },
                                { red: 35, grey: 65 },
                                { red: 50, grey: 50 },
                                { red: 30, grey: 70 },
                                { red: 45, grey: 55 },
                                { red: 40, grey: 60 }
                            ]}
                        />

                        <AnalyticsCard
                            title="Total Users"
                            value="500"
                            change={{
                                value: '-4%',
                                isPositive: false,
                                text: 'From last month'
                            }}
                            icon="groupIcon"
                            chartData={[
                                { red: 25, grey: 75 },
                                { red: 40, grey: 60 },
                                { red: 35, grey: 65 },
                                { red: 50, grey: 50 },
                                { red: 30, grey: 70 },
                                { red: 45, grey: 55 },
                                { red: 40, grey: 60 }
                            ]}
                        />
                    </KPICardsGrid>

                    <ChartCard $totalRevenue>
                        <TotalRevenueHeader>
                            <TotalRevenueContent>
                                <ChartCardValue $totalRevenue>$682.5</ChartCardValue>
                                <TotalRevenueTitleRow>
                                    <ChartCardTitle $totalRevenue>Total Revenue</ChartCardTitle>
                                    <ChartCardChange $totalRevenue $positive>
                                        <img src={getIcon('arrowUp')} alt="Up" /> +2.45%
                                    </ChartCardChange>
                                </TotalRevenueTitleRow>
                            </TotalRevenueContent>
                            <TotalRevenueIcon>
                                <img src={getIcon('barIcon')} alt="Revenue" />
                            </TotalRevenueIcon>
                        </TotalRevenueHeader>
                        <RevenueChartContainer>
                            <RevenueChartYAxis>
                                {yAxisLabels.map((labelValue, i) => {
                                    // Calculate position to align with circle centers
                                    // Circles are positioned at: bottomPadding + (i * (circleHeight + gap))
                                    // Circle center is at: bottomPosition + (circleHeight / 2)
                                    const circleHeight = circleSize;
                                    const circleGap = gap;
                                    // Responsive bottom padding for X-axis
                                    const responsiveBottomPadding = window.innerWidth <= 480 ? 3 : 5;

                                    // Map label index to circle index (both are 0-5, 6 total)
                                    // Labels are from top to bottom: [max, ..., $20]
                                    // Circles are from top to bottom: [5, 4, 3, 2, 1, 0]
                                    // Label at index i corresponds to circle at index (maxLabels - 1 - i)
                                    const circleIndex = maxLabels - 1 - i;
                                    const circleBottom = responsiveBottomPadding + (circleIndex * (circleHeight + circleGap));
                                    const circleCenter = circleBottom + (circleHeight / 2);

                                    return (
                                        <RevenueChartYAxisLabel
                                            key={i}
                                            style={{
                                                bottom: `${circleCenter}px`
                                            }}
                                        >
                                            ${labelValue}
                                        </RevenueChartYAxisLabel>
                                    );
                                })}
                            </RevenueChartYAxis>
                            <DotChart>
                                {revenueData.map((item, index) => {
                                    const circleCount = item.circles;



                                    return (
                                        <DotChartColumn key={index}>
                                            <div
                                                style={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'flex-end',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                {Array.from({ length: 6 }, (_, i) => {
                                                    // Y-axis has 6 labels with space-between: $20, $40, $60, $80, $100, $max
                                                    // Each circle represents $20, so circle i should align with label at index i
                                                    // Circles have 4px gap between them
                                                    // Bottom circle (i=0) aligns with $20 label, second (i=1) with $40, etc.
                                                    // Show black circles for actual data, grey circles for remaining positions

                                                    const isFilled = i < circleCount; // Black circle if within data range
                                                    const circleHeight = circleSize; // Responsive circle height
                                                    const circleGap = gap; // Responsive gap between circles
                                                    const responsiveBottomPadding = window.innerWidth <= 480 ? 3 : 5; // Responsive padding from X-axis

                                                    // Calculate position: stack circles with responsive gap
                                                    // Position from bottom = responsiveBottomPadding + (i * (circleHeight + circleGap))
                                                    const bottomPosition = responsiveBottomPadding + (i * (circleHeight + circleGap));

                                                    return (
                                                        <div
                                                            key={i}
                                                            style={{
                                                                position: 'absolute',
                                                                bottom: `${bottomPosition}px`,
                                                                width: `${circleSize}px`,
                                                                height: `${circleSize}px`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                margin: 0,
                                                                padding: 0
                                                            }}
                                                        >
                                                            <DotChartDot $filled={isFilled} />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <DotChartMonthLabel>{item.month}</DotChartMonthLabel>
                                        </DotChartColumn>
                                    );
                                })}
                            </DotChart>
                        </RevenueChartContainer>
                    </ChartCard>
                </LeftColumn>

                <ChartsColumn>
                    <ChartCard $dark>
                        <DonutChartHeader $dark>
                            <DonutChartHeaderLeft>
                                <DonutChartHeaderIcon>
                                    <img src={getIcon('revenueIcon')} alt="Revenue" />
                                </DonutChartHeaderIcon>
                            </DonutChartHeaderLeft>
                            <DonutChartHeaderRight>
                                <DonutChartHeaderButton>
                                    <FilterOutlined />
                                </DonutChartHeaderButton>
                                <DonutChartHeaderButton>
                                    <MoreOutlined />
                                </DonutChartHeaderButton>
                            </DonutChartHeaderRight>
                        </DonutChartHeader>
                        <ChartCardTitle $dark>Top Performing Plans</ChartCardTitle>
                        <DonutChart data={donutData} $dark />
                    </ChartCard>

                    <ChartCard $red>
                        <DonutChartHeader>
                            <DonutChartHeaderLeft>
                                <DonutChartHeaderIcon $red>
                                    <img src={getIcon('revenueIcon')} alt="Revenue" />
                                </DonutChartHeaderIcon>
                            </DonutChartHeaderLeft>
                            <DonutChartHeaderRight>
                                <DonutChartHeaderButton $red>
                                    <FilterOutlined />
                                </DonutChartHeaderButton>
                                <DonutChartHeaderButton $red>
                                    <MoreOutlined />
                                </DonutChartHeaderButton>
                            </DonutChartHeaderRight>
                        </DonutChartHeader>
                        <RevenueCountryTitle>Revenue by Country</RevenueCountryTitle>
                        <CountryChartContainer>
                            <CountrySpacer />
                            {countryData.map((item, index) => (
                                <CountryItem key={index}>
                                    <CountryItemTop>
                                        <CountryItemLeft>
                                            <CountryFlag src={getIcon(item.flag)} alt={item.country} />
                                            <CountryName>{item.country}</CountryName>
                                        </CountryItemLeft>
                                        <CountryRevenue>${item.displayValue}</CountryRevenue>
                                    </CountryItemTop>
                                    <CountryProgressBar>
                                        <CountryProgressFill $width={(item.value / maxCountryValue) * 100} />
                                    </CountryProgressBar>
                                </CountryItem>
                            ))}
                        </CountryChartContainer>
                    </ChartCard>
                </ChartsColumn>
            </FirstRowContainer>


            <Table
                title="Tenants"
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showFilter={true}
                showMore={true}
                pagination={true}
                pageSize={10}
                showSearch={true}
                showPagination={true}
            />
        </DashboardContainer>
    );
};

export default Dashboard;

